// -----------------------------------------------------------------------------
// A7 ADMIN CONSOLE — Part 3 (v0.3.0) + round 16 (v0.3.1, boards approved 10/Oct/26)
//   Tabs: Projects (A7) · Members (A7.2) · Roles & access (A7.3) · App settings (A7.5, v0.6.0: root super admin only)
//   * A7.1 Manage members and A7.2.2 Manage projects: one pop-up each, an editable "on it" section on top,
//     everyone / every project else below (scrolls), one Save; closing with changes asks Save / Discard / Keep.
//   * Company roles (GM, Project head) are set per person; BIM Coordinator / BIM Modeller per project.
//   * v0.8.0 (round 28, I1): the A7.4 limited Members page is gone — people with the Project setup switch manage
//     members in Project setup › 👥 Manage members (A7.1), which now shows Status, Last active and Resend invite.
//   * v0.8.0: Add & send invite / Resend invite / Reset password send real emails (core/api/backend.ts `auth`).
//   * × and Esc leave the whole console (round 16 #10).
// Every rule is checked again by the database (0008, 0009); the screens only shape the work.
// Help text sits in grey (i) icons only (working rule 10/Oct/26).
// -----------------------------------------------------------------------------
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { auth, rpc } from "../api/backend";
import { inviteToast } from "../authLinks";
import { useRpc } from "../api/useRpc";
import { useSession } from "../session";
import { href, leaveAdmin, type Route } from "../router";
import { formatDate } from "../format";
import { nextSort, sortArrow, sortBy, type SortState } from "../lists";
import { useListKeys } from "../useListKeys";
import { useEsc } from "../esc";
import { requestLeave, useLeaveGuard } from "../leaveGuard";
import { MODULES } from "../../modules/registry";
import {
  addMemberProblems, changedRoles, COMPANY_ROLES, companyLabel, copySummary, EVERY_PROJECT, memberMatches, missingRoles,
  pickChanges, projectProblems, roleChangeText, STATUS_PILL, type Assignment, type PickRow,
} from "../admin";
import { CodeChip, ErrorBox, FilterDropdown, InfoIcon, Loading, Modal, SearchBox, SortTh, Switch, toast, useAskClose } from "../ui/kit";
import type { Access, AdminMembers, AdminPerson, AdminProjects, AdminRole, IdName, Unassigned } from "../api/types";
import { NotFoundPage } from "./SimplePages";
import { Icon } from "../ui/icons";
import { BulkEditModal } from "./BulkEdit";

type Tab = "projects" | "members" | "roles" | "settings";
type AdminRoute = Extract<Route, { name: "admin" }>;
const TAB_NAME: Record<Tab, string> = { projects: "Projects", members: "Members", roles: "Roles & access", settings: "App settings" };
/** GitHub › Actions › Demo data (.github/workflows/demo.yml) — Load / Remove run there (round 23, Option 1). */
export const DEMO_WORKFLOW_URL = "https://github.com/bimofficetech/BuildNexx/actions/workflows/demo.yml";
const built = (id: string) => MODULES.some((m) => m.id === id);
const errText = (e: unknown) => (e as Error).message;
const projectRoles = (roles: IdName[]) => roles.filter((r) => r.kind !== "company");

const TIPS = {
  projects: ["Projects are shared by every BuildNexx module.", "Only the super admin adds projects, sets their name and code, and assigns members."],
  code: ["Format BIM-XXX (3 digits), typed in.", "Must be unique.", "Shown in the app only, never on PDFs."],
  clientOwner: ["Both print on the PDF cover page.", "Changed only here (super admin, Administrators).", "Project setup can propose a change; it waits under Notifications › Pending action until approved or rejected."],
  members: ["Who is on this project and their role; their access follows the role (Roles & access).", "GM and the super admin are on every project automatically.", "Change roles, remove or add people in 👥 Manage members."],
  addMember: ["For someone new to BuildNexx: name and email.", "Company role first: Administrator (super admin only), GM, Project head or Project member.", "Projects are optional: tick them in Other projects and pick a role on each row (the same picker as Manage projects); or add them later.", "GM and Administrator are on every project, so the list is hidden for them.", "Add & send invite emails them a link to set their own password (works for 24 hours).", "Until they set it, they show as Invited; Resend invite sends a fresh link."],
  manageMembers: ["Top: people on this project — change a role or remove with ×.", "Below: everyone else — tick to add and pick a role on each row.", "GM, Project head and super admin rows show their company role, no dropdown.", "+ Add member: someone new to BuildNexx (Add member pop-up, this project filled in).", "Save changes saves everything at once; closing with changes asks Save, Discard or Keep editing.", "Status: Active, Invited or Deactivated; Last active: the day they last used BuildNexx.", "Invited: Resend invite emails a fresh link (works for 24 hours).", "Project heads manage their project's members here, from Project setup › Manage members (the separate Members page is gone).", "Click a section heading (▾) to collapse or expand it; the lower section scrolls."],
  manageProjects: ["Top: projects this person is on — change the role or remove with ×.", "Below: other projects — tick to add and pick a role on each row.", "A project head's rows have no role dropdown: they lead every project they are on.", "Save changes saves everything at once; closing with changes asks Save, Discard or Keep editing.", "Click a section heading (▾) to collapse or expand it; the lower section scrolls."],
  membersTab: ["Everyone in BuildNexx and how many projects they are on.", "Click a person, or their Invited status, to open them.", "Removing someone from a project keeps their account and history."],
  signIn: "Off = deactivated (people who have left); their records stay.",
  mail: ["Invited: Resend invite emails a fresh link (date = last one sent).", "Active: Reset password emails a link to set a new password.", "Links work for 24 hours; emails come from BuildNexx (noreply.bimoffice.com.my).", "People can also reset it themselves: Sign in › Forgot password?"],
  remove: ["Never signed in (Invited): deleted completely, nothing is left.", "Has used BuildNexx: the login is deleted; their issues, log entries and Activity stay and show \"Removed member\".", "Always asks first and cannot be undone.", "Someone who left but may return: turn Can sign in off instead.", "The super admin and you yourself cannot be removed; only the super admin removes an Administrator."],
  company: ["Administrator: everything except appointing Administrators (super admin only).", "GM: edits every project.", "Project head: edits theirs, views the rest.", "Project member: role picked per project.", "A change always asks first."],
  settings: ["App-wide settings, for the super admin only."],
  storage: ["Snapshots, plan PDFs, cover photos and logos are kept in Supabase Storage.", "Free plan: 1 GB in total; new uploads stop when it is full.", "Pro plan: 100 GB included.", "Snapshots are shrunk to about 300 KB before they are uploaded."],
  demo: ["Demo projects BIM-991 and BIM-992 (small) and BIM-993 (the full CHGP x Divine KL sample), plus one demo login per role.", "Demo logins: name@demo.buildnexx with the shared demo password (kept in GitHub).", "Demo logins see only demo projects; your staff never see them; you see both.", "Load and Remove run from GitHub › Actions › Demo data; Remove deletes the demo projects and logins only — real data is never touched.", "Demo people are not listed under Members."],
  roles: ["What each role can do in each module.", "Company roles (GM, Project head) apply on every project; project roles are picked per project.", "Admin console: super admin and Administrators."],
  access: ["Edit: view everything, add and change records, export.", "View: read only; can open and export PDFs.", "No access: hidden from the sidebar and dashboard."],
  signoff: "Can mark a published component version as signed off; the person who does it is logged.",
  setup: "Can open Project setup; the super admin and Administrators always can.",
  addRole: "Starts with the access and switches of the role you copy; can be deleted only while nobody holds it.",
  unassigned: ["What the role sees on projects the person is not on: Edit, View (tagged Not assigned) or Hidden.", "GM is on every project automatically."],
};

export function AdminPage({ route }: { route: AdminRoute }) {
  const { me } = useSession();
  if (!me.is_admin) return <NotFoundPage />;   // super admin / Administrators only (I1, v0.8.0: no limited Members page)
  const tab: Tab = route.tab ?? "projects";
  if (tab === "projects") return <ProjectsTab />;
  if (tab === "roles") return <RolesTab />;
  if (tab === "settings") return me.is_super_admin ? <SettingsTab /> : <NotFoundPage />;
  return <MembersTab route={route} />;
}

/** Crumb + actions + tab strip. */
function AdminHeader({ tab, actions }: { tab: Tab; actions?: ReactNode }) {
  const { me } = useSession();
  const tabs: Tab[] = me.is_super_admin ? ["projects", "members", "roles", "settings"] : ["projects", "members", "roles"];
  const tip = tab === "projects" ? TIPS.projects : tab === "members" ? TIPS.membersTab : tab === "settings" ? TIPS.settings : TIPS.roles;
  return (
    <header className="page-header admin-head">
      <div className="crumb-row">
        <p className="eyebrow">Admin console › {TAB_NAME[tab]}</p>
        <div className="actions">{actions}
          <button className="btn icon close-x" aria-label="Close" onClick={() => leaveAdmin()}><Icon name="close" /></button>
        </div>
      </div>
      <nav className="tabs" aria-label="Admin console">
        {tabs.map((t) => t === tab
          ? <span key={t} className="tab on" aria-current="page">{TAB_NAME[t]} <InfoIcon tip={tip} /></span>
          : <a key={t} className="tab" href={href({ name: "admin", tab: t === "projects" ? undefined : t })}>{TAB_NAME[t]}</a>)}
      </nav>
    </header>
  );
}

// ============================== A7.5 App settings (root super admin) ==============================
// Demo data card (0014 admin_demo_status). Load / Remove run in GitHub (no button here can touch the live database
// with the service key). File storage card (0015 admin_storage_status): what Part 6 files use of the Free plan's 1 GB.

function SettingsTab() {
  const { data, error } = useRpc("admin_demo_status");
  return (
    <>
      <AdminHeader tab="settings" />
      {error && <ErrorBox message={error} />}
      {!data ? (!error && <Loading />) : (
        <div className="settings-grid">
          <section className="card settings-card" data-testid="demo-card" data-demo-card={data.loaded ? "loaded" : "empty"}>
            <div className="row between"><h3 className="row gap6">Demo data <InfoIcon tip={TIPS.demo} /></h3>
              <span className={`pill ${data.loaded ? "p-closed" : "p-unassigned"}`}>{data.loaded ? "Loaded" : "Not loaded"}</span></div>
            <div className="settings-stats">
              <div className="field-card"><p className="k">Projects</p><p className="v">{data.projects.length}</p></div>
              <div className="field-card"><p className="k">Demo logins</p><p className="v">{data.logins.length}</p></div>
              <div className="field-card"><p className="k">Issues</p><p className="v">{data.issues}</p></div>
            </div>
            {data.logins.length > 0 && (
              <table className="fixed-cols"><colgroup><col style={{ width: 190 }} /><col /></colgroup>
                <thead><tr><th>Demo login</th><th>Role</th></tr></thead>
                <tbody>{data.logins.map((l) => (
                  <tr key={l.email} data-demo-login={l.email.split("@")[0]}><td>{l.email}</td>
                    <td className="muted">{l.role}{l.projects.length > 0 && <> · {l.projects.map((c, i) => (i ? c.replace("BIM-", "") : c)).join(", ")}</>}</td></tr>))}
                </tbody></table>)}
            <div className="row between">
              <span className="small muted">{data.loaded ? <>Loaded {formatDate(data.loaded_on)} · {data.projects.map((p) => p.code).join(", ")}</> : "No demo data in this app"}</span>
              <a className="btn" href={DEMO_WORKFLOW_URL} target="_blank" rel="noreferrer">Load / Remove in GitHub <Icon name="open_in_new" /></a>
            </div>
          </section>
          <StorageCard />
        </div>
      )}
    </>
  );
}

const GB = 1024 ** 3;
function StorageCard() {
  const { data } = useRpc("admin_storage_status");
  if (!data) return <section className="card settings-card"><Loading /></section>;
  const used = Number(data.snapshots) + Number(data.plans) + Number(data.cover ?? 0), cap = Number(data.limit_bytes);   // v0.7.0: + cover pictures
  const pct = (n: number) => `${Math.min(100, (Number(n) / cap) * 100)}%`;
  return (
    <section className="card settings-card" data-testid="storage-card" data-storage-card={`${(used / GB).toFixed(2)}:${(cap / GB).toFixed(0)}`}>
      <div className="row between"><h3 className="row gap6">File storage <InfoIcon tip={TIPS.storage} /></h3><span className="pill p-unassigned">Free plan</span></div>
      <p className="storage-used"><b>{(used / GB).toFixed(2)} GB</b> <span className="muted">of {(cap / GB).toFixed(0)} GB used</span></p>
      <div className="storage-bar"><span style={{ width: pct(data.snapshots), background: "#B9481F" }} /><span style={{ width: pct(data.plans), background: "#378ADD" }} /><span style={{ width: pct(data.cover ?? 0), background: "#7F77DD" }} /></div>
      <div className="storage-key">
        <span><i style={{ background: "#B9481F" }} />Snapshots {(Number(data.snapshots) / GB).toFixed(2)} GB</span>
        <span><i style={{ background: "#378ADD" }} />Plans {(Number(data.plans) / GB).toFixed(2)} GB</span>
        <span><i style={{ background: "#7F77DD" }} />Photos &amp; logos {(Number(data.cover ?? 0) / GB).toFixed(2)} GB</span>
      </div>
    </section>
  );
}

// ============================== A7 Projects ==============================

function ProjectsTab() {
  const { data, error, reload } = useRpc("admin_projects");
  const [sel, setSel] = useState<string | null>(null);          // project id, "new", or null
  const [hi, setHi] = useState<number | null>(null);
  const [managing, setManaging] = useState<string | null>(null);   // A7.1 for this project code
  const [bulk, setBulk] = useState(false);                          // v0.9.0 A7.6 Bulk edit
  useEffect(() => { if (data && sel === null && data.projects[0]) setSel(data.projects[0].id); }, [data, sel]);
  const rows = data?.projects ?? [];
  const pick = (id: string | null) => requestLeave(() => setSel(id));      // a panel with edits asks first (v0.4.3)
  useListKeys({ count: rows.length, hi, setHi, onEnter: (i) => pick(rows[i].id), active: !managing });
  const current = rows.find((p) => p.id === sel);
  return (
    <>
      <AdminHeader tab="projects" actions={<>
        <button className="btn" disabled={!data} onClick={() => requestLeave(() => setBulk(true))} data-testid="bulk-edit"><Icon name="table_view" /> Bulk edit</button>
        <button className="btn primary" onClick={() => pick("new")}><Icon name="add" /> Add project</button></>} />
      {error && <ErrorBox message={error} />}
      {!data ? (!error && <Loading />) : (
        <div className="admin-grid">
          <div className="card list-card">
            <table className="fixed-cols" data-testid="admin-projects">
              <colgroup><col style={{ width: 84 }} /><col /><col style={{ width: 150 }} /><col style={{ width: 70 }} /><col style={{ width: 110 }} /><col style={{ width: 84 }} /><col style={{ width: 36 }} /></colgroup>
              <thead><tr><th>Code</th><th>Project name</th><th>Client</th><th className="num">Members</th><th>Modules</th><th>Created</th><th /></tr></thead>
              <tbody>{rows.map((p, k) => (
                <tr key={p.id} data-row={k} className={`clickable${p.id === sel ? " active" : ""}${k === hi ? " kbd" : ""}`} onClick={() => pick(p.id)}>
                  <td><CodeChip code={p.code} />{p.is_demo && <> <span className="demo-tag" data-demo="1">(Demo)</span></>}</td><td className="strong">{p.name}</td><td>{p.client}</td>
                  <td className="num" data-testid="member-count">{p.member_count}</td>
                  <td>{p.modules.map((m) => <span key={m} className="pill p-mod">{data.modules.find((x) => x.id === m)?.name ?? m}</span>)}</td>
                  <td className="nowrap">{formatDate(p.created_at)}</td><td className="accent-text">Edit</td>
                </tr>))}
              </tbody>
            </table>
          </div>
          {(sel === "new" || current) && <ProjectPanel key={sel!} data={data} id={sel === "new" ? null : sel}
            onCancel={() => setSel(current ? current.id : rows[0]?.id ?? null)}
            onSaved={(id) => { reload(); setSel(id); }} onManage={(code) => setManaging(code)} />}
        </div>
      )}
      {managing && <ManageMembersModal code={managing} onClose={() => setManaging(null)} onSaved={() => { setManaging(null); reload(); }} />}
      {bulk && data && <BulkEditModal data={data} onClose={() => setBulk(false)} onApplied={() => { setBulk(false); reload(); }} />}
    </>
  );
}

function ProjectPanel({ data, id, onCancel, onSaved, onManage }: {
  data: AdminProjects; id: string | null; onCancel: () => void; onSaved: (id: string) => void; onManage: (code: string) => void;
}) {
  const p = data.projects.find((x) => x.id === id);
  const [name, setName] = useState(p?.name ?? "");
  const [code, setCode] = useState(p?.code ?? "");
  const [client, setClient] = useState(p?.client ?? "");
  const [owner, setOwner] = useState(p?.owner ?? "");
  const [mods, setMods] = useState<string[]>(p?.modules ?? MODULES.map((m) => m.id));
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const start = JSON.stringify([p?.name ?? "", p?.code ?? "", p?.client ?? "", p?.owner ?? "", p?.modules ?? MODULES.map((m) => m.id)]);
  const [saved, setSaved] = useState(false);
  const dirty = !saved && JSON.stringify([name, code, client, owner, mods]) !== start;
  const save = async (): Promise<boolean> => {
    const problems = projectProblems(code, name);
    if (problems.length) { setErr(problems.join(" · ")); return false; }
    setBusy(true); setErr(null);
    try {
      const r = await rpc("admin_project_save", { id: id ?? undefined, code: code.trim().toUpperCase(), name: name.trim(), client: client.trim(), owner: owner.trim(), modules: mods });
      setBusy(false); setSaved(true); toast("Saved");
      onSaved(r.id);
      return true;
    } catch (e) { setErr(errText(e)); setBusy(false); return false; }
  };
  useLeaveGuard(dirty, save);                                    // v0.4.3: leaving with edits asks first
  return (
    <aside className="card admin-panel" aria-label={id ? "Edit project" : "Add project"}>
      <h2>{id ? "Edit project" : "Add project"}</h2>
      <div className="panel-body">
        <label className="field"><span>Project name</span><input value={name} onChange={(e) => setName(e.target.value)} aria-label="Project name" /></label>
        <label className="field"><span className="field-label">Project code <InfoIcon tip={TIPS.code} /></span>
          <input className="code-input" value={code} placeholder="BIM-000" onChange={(e) => setCode(e.target.value.toUpperCase())} aria-label="Project code" /></label>
        <label className="field"><span className="field-label">Client <InfoIcon tip={TIPS.clientOwner} /></span><input value={client} onChange={(e) => setClient(e.target.value)} aria-label="Client" /></label>
        <label className="field"><span>Project owner</span><input value={owner} onChange={(e) => setOwner(e.target.value)} aria-label="Project owner" /></label>
        <div className="field"><span>Modules</span>
          {data.modules.map((m) => (
            <label key={m.id} className={`check${built(m.id) ? "" : " muted"}`}>
              <input type="checkbox" disabled={!built(m.id)} checked={built(m.id) && mods.includes(m.id)}
                onChange={(e) => setMods(e.target.checked ? [...mods, m.id] : mods.filter((x) => x !== m.id))} />
              {m.name}{!built(m.id) && <span className="soon">soon</span>}
            </label>))}
        </div>
        {p && <div className="field">
          <div className="row between"><span className="field-label">Members ({p.member_count}) <InfoIcon tip={TIPS.members} /></span>
            <button type="button" className="btn slim-btn" onClick={() => onManage(p.code)}><Icon name="group" /> Manage members</button></div>
          <ul className="member-list" data-testid="project-members">
            {p.members.map((m) => <li key={m.user_id}><span>{m.name}</span><span className="muted">{m.role}</span></li>)}
          </ul>
        </div>}
        {err && <ErrorBox message={err} />}
      </div>
      <div className="panel-foot">
        <button className="btn" onClick={() => requestLeave(onCancel)}>Cancel</button>
        <button className="btn primary" disabled={busy} onClick={save}>{id ? "Save changes" : "Add project"}</button>
      </div>
    </aside>
  );
}

// ============================== pick pop-ups (A7.1 Manage members, A7.2.2 Manage projects) ==============================

/** Collapsible section heading (▾ / ▸). */
function Section({ title, open, onToggle, children, scroll }: { title: string; open: boolean; onToggle: () => void; children: ReactNode; scroll?: boolean }) {
  return (
    <div className={`pick-section${scroll ? " grow" : ""}`}>
      <button type="button" className="pick-head" aria-expanded={open} onClick={onToggle}><span className="chev"><Icon name={open ? "expand_more" : "chevron_right"} /></span>{title}</button>
      {open && <div className={`pick-box${scroll ? " scroll" : ""}`}>{children}</div>}
    </div>
  );
}

/** One row: [tick or space] name … role (dropdown or fixed text) [× / Undo]. */
function PickLine({ tick, label, name, extra, meta, role, roleFixed, roles, onRole, onTick, onRemove, removed, onUndo, disabled, changed }: {
  tick?: boolean | "space"; label: ReactNode; /** v0.9.2: words for screen readers / tests when label is not plain text */ name?: string; extra?: ReactNode; /** v0.8.0 A7.1: Status + Last active cells */ meta?: ReactNode;
  role?: string; roleFixed?: ReactNode; roles?: IdName[];
  onRole?: (v: string) => void; onTick?: (v: boolean) => void; onRemove?: () => void; removed?: boolean; onUndo?: () => void; disabled?: boolean; changed?: boolean;
}) {
  return (
    <div className={`pick-row${removed ? " removed" : ""}${disabled ? " disabled" : ""}`}>
      <span className="pick-tick">{typeof tick === "boolean" &&
        <input type="checkbox" checked={tick} disabled={disabled} aria-label={`Add ${name ?? (typeof label === "string" ? label : "")}`} onChange={(e) => onTick?.(e.target.checked)} />}</span>
      <span className="pick-name">{label}{extra}</span>
      {meta}
      {removed
        ? <button type="button" className="btn link accent pick-undo" onClick={onUndo}>Undo</button>
        : roleFixed !== undefined
          ? <span className="pick-role fixed">{roleFixed}</span>
          : <select className={`pick-role${changed ? " changed" : ""}`} value={role ?? ""} disabled={disabled}
              aria-label={`Role of ${name ?? (typeof label === "string" ? label : "row")}`} onChange={(e) => onRole?.(e.target.value)}>
              <option value="">Pick a role…</option>
              {(roles ?? []).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>}
      <span className="pick-x">{onRemove && !removed && <button type="button" className="btn icon mini" aria-label={`Remove ${name ?? (typeof label === "string" ? label : "")}`} onClick={onRemove}><Icon name="close" /></button>}</span>
    </div>
  );
}

/** v0.8.0 (round 28, I1) A7.1 Status + Last active cells; an Invited person's Last active cell is "Resend invite". */
function MemberMeta({ p, onResend }: { p: AdminPerson; onResend: (p: AdminPerson) => void }) {
  return <>
    <span className="pick-status" data-status={p.status}>{p.status === "Active" ? <span className="muted">Active</span> : <span className={`pill ${STATUS_PILL[p.status]}`}>{p.status}</span>}</span>
    <span className="pick-last">{p.status === "Invited"
      ? <button type="button" className="btn link accent" onClick={() => onResend(p)} aria-label={`Resend invite to ${p.name}`}>Resend invite</button>
      : <span className="muted">{p.last_active ? formatDate(p.last_active) : "–"}</span>}</span>
  </>;
}
const MemberCols = () => (
  <div className="pick-cols" aria-hidden="true"><span className="pick-tick" /><span className="pick-name">Name</span>
    <span className="pick-status">Status</span><span className="pick-last">Last active</span><span className="pick-role-h">Role</span><span className="pick-x" /></div>
);

/** Close guard for the pick pop-ups: with changes, ask Save / Discard / Keep editing. */
const usePickClose = (dirty: boolean, onClose: () => void, save: () => void) => useAskClose(dirty, onClose, save);

/** A7.1 Manage members — from A7 Edit project (and A6 Project setup in Part 4). */
export function ManageMembersModal({ code, onClose, onSaved }: { code: string; onClose: () => void; onSaved: () => void }) {
  const { data, error, reload } = useRpc("admin_members");
  const [rows, setRows] = useState<PickRow[] | null>(null);
  const [q, setQ] = useState("");
  const [openTop, setOpenTop] = useState(true);
  const [openOthers, setOpenOthers] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const people = data?.people ?? [];
  const byId = useMemo(() => new Map(people.map((p) => [p.id, p])), [people]);
  useEffect(() => {
    if (!data) return;
    setRows((prev) => data.people.map((p) => {
      const on = p.projects.find((x) => x.code === code);
      const was = { on: !!on, role: on?.role_id ?? "" };
      const old = prev?.find((r) => r.key === p.id);
      return old && old.was.on === was.on ? old : { key: p.id, on: was.on, role: was.role, was };
    }));
  }, [data, code]);
  const dirty = !!rows && (pickChanges(rows).set.length + pickChanges(rows).remove.length) > 0;
  const fixedRole = (p: AdminPerson) => (p.is_super_admin ? "Super admin" : p.company_role ? companyLabel(p.company_role) : null);   // incl. Administrator
  const save = async () => {
    if (!rows) return;
    const missing = missingRoles(rows, (k) => !fixedRole(byId.get(k)!));
    if (missing.length) return setErr(`Pick a role for ${missing.map((k) => byId.get(k)?.name).join(", ")}`);
    const ch = pickChanges(rows);
    setBusy(true); setErr(null);
    try {
      await rpc("admin_project_members_save", { code, set: ch.set.map((s) => ({ user_id: s.key, role_id: s.role || null })), remove: ch.remove });
      onSaved();
    } catch (e) { setErr(errText(e)); setBusy(false); }
  };
  const { close, confirm } = usePickClose(dirty, onClose, save);
  const set = (key: string, patch: Partial<PickRow>) => setRows((rs) => rs!.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const match = (p: AdminPerson) => `${p.name} ${p.email}`.toLowerCase().includes(q.trim().toLowerCase());
  const rank = (p: AdminPerson) => (p.is_super_admin ? 0 : p.company_role === "admin" ? 1 : p.company_role === "gm" ? 2 : p.company_role === "project_head" ? 3 : 4);
  const top = (rows ?? []).filter((r) => r.was.on).map((r) => ({ r, p: byId.get(r.key)! })).filter(({ p }) => p && match(p))
    .sort((a, b) => rank(a.p) - rank(b.p) || a.p.name.localeCompare(b.p.name));
  const others = (rows ?? []).filter((r) => !r.was.on).map((r) => ({ r, p: byId.get(r.key)! })).filter(({ p }) => p && match(p));
  const roles = projectRoles(data?.roles ?? []);
  const resend = async (p: AdminPerson) => {
    setErr(null);
    try { const r = await auth.resendInvite(p.id); toast(inviteToast(r, true)); reload(); } catch (e) { setErr(errText(e)); }
  };
  return (
    <Modal title={`Manage members · ${code}`} info={TIPS.manageMembers} onClose={close} className="pick-modal">
      {error && <ErrorBox message={error} />}
      <SearchBox placeholder="Search name or email" value={q} onChange={setQ} />
      {!rows ? <Loading /> : <>
        <MemberCols />
        <Section title={`On this project (${top.length})`} open={openTop} onToggle={() => setOpenTop(!openTop)}>
          {top.map(({ r, p }) => {
            const fixed = fixedRole(p);
            const auto = p.is_super_admin || EVERY_PROJECT(p.company_role);
            return <PickLine key={r.key} tick="space" label={p.name} meta={<MemberMeta p={p} onResend={resend} />} role={r.role} roles={roles} roleFixed={fixed ?? undefined} changed={r.role !== r.was.role}
              removed={!r.on} onUndo={() => set(r.key, { on: true, role: r.was.role })} onRole={(v) => set(r.key, { role: v })}
              onRemove={auto ? undefined : () => set(r.key, { on: false })} />;
          })}
        </Section>
        <Section title={`Others (${others.length})`} open={openOthers} onToggle={() => setOpenOthers(!openOthers)} scroll>
          {others.map(({ r, p }) => {
            const fixed = fixedRole(p);
            const off = !p.active;
            return <PickLine key={r.key} tick={r.on} label={p.name} disabled={off} meta={<MemberMeta p={p} onResend={resend} />}
              role={r.role} roles={roles} roleFixed={off ? "" : fixed ?? undefined}
              onTick={(v) => set(r.key, { on: v })} onRole={(v) => set(r.key, { role: v, on: true })} />;
          })}
          {others.length === 0 && <p className="muted small pad">No one else matches</p>}
        </Section>
      </>}
      {err && <ErrorBox message={err} />}
      <div className="modal-foot">
        <button className="btn accent-outline" onClick={() => setAdding(true)}><Icon name="add" /> Add member</button><span className="grow" />
        <button className="btn" onClick={close}>Cancel</button>
        <button className="btn primary" disabled={busy || !dirty} onClick={save}>Save changes</button>
      </div>
      {confirm}
      {adding && <AddMemberModal prefill={code} onClose={() => setAdding(false)} onDone={() => { setAdding(false); reload(); }} />}
    </Modal>
  );
}

/** A7.2.2 Manage projects — from A7.2 Edit member › Manage. */
function ManageProjectsModal({ person, data, onClose, onSaved }: { person: AdminPerson; data: AdminMembers; onClose: () => void; onSaved: () => void }) {
  const head = person.company_role === "project_head";
  const [rows, setRows] = useState<PickRow[]>(() => data.projects.map((pr) => {
    const on = person.projects.find((x) => x.code === pr.code);
    const was = { on: !!on, role: on?.role_id ?? "" };
    return { key: pr.code, on: was.on, role: was.role, was };
  }));
  const [q, setQ] = useState("");
  const [openTop, setOpenTop] = useState(true);
  const [openOthers, setOpenOthers] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ch = pickChanges(rows);
  const dirty = ch.set.length + ch.remove.length > 0;
  const name = (c: string) => data.projects.find((p) => p.code === c)?.name ?? c;
  const save = async () => {
    const missing = head ? [] : missingRoles(rows, () => true);
    if (missing.length) return setErr(`Pick a role for ${missing.join(", ")}`);
    setBusy(true); setErr(null);
    try {
      await rpc("admin_member_update", { id: person.id, assignments: rows.filter((r) => r.on).map((r) => ({ code: r.key, role_id: r.role })) });
      onSaved();
    } catch (e) { setErr(errText(e)); setBusy(false); }
  };
  const { close, confirm } = usePickClose(dirty, onClose, save);
  const set = (key: string, patch: Partial<PickRow>) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const match = (c: string) => `${c} ${name(c)}`.toLowerCase().includes(q.trim().toLowerCase());
  const label = (c: string) => <><b className="pick-code">{c}</b><span className="ellipsis">{name(c)}</span></>;
  const top = rows.filter((r) => r.was.on && match(r.key));
  const others = rows.filter((r) => !r.was.on && match(r.key));
  const roles = projectRoles(data.roles);
  return (
    <Modal title={`Manage projects · ${person.name}`} info={TIPS.manageProjects} onClose={close} className="pick-modal">
      <SearchBox placeholder="Search code or project" value={q} onChange={setQ} />
      <Section title={`Assigned (${top.length})`} open={openTop} onToggle={() => setOpenTop(!openTop)}>
        {top.map((r) => <PickLine key={r.key} name={r.key} tick="space" label={label(r.key)} role={r.role} roles={roles} roleFixed={head ? "Project head" : undefined}
          changed={r.role !== r.was.role} removed={!r.on} onUndo={() => set(r.key, { on: true, role: r.was.role })}
          onRole={(v) => set(r.key, { role: v })} onRemove={() => set(r.key, { on: false })} />)}
        {top.length === 0 && <p className="muted small pad">Not on any project yet</p>}
      </Section>
      <Section title={`Other projects (${others.length})`} open={openOthers} onToggle={() => setOpenOthers(!openOthers)} scroll>
        {others.map((r) => <PickLine key={r.key} name={r.key} tick={r.on} label={label(r.key)} role={r.role} roles={roles} roleFixed={head ? "Project head" : undefined}
          onTick={(v) => set(r.key, { on: v })} onRole={(v) => set(r.key, { role: v, on: true })} />)}
      </Section>
      {err && <ErrorBox message={err} />}
      <div className="modal-foot">
        <button className="btn" onClick={close}>Cancel</button>
        <button className="btn primary" disabled={busy || !dirty} onClick={save}>Save changes</button>
      </div>
      {confirm}
    </Modal>
  );
}

// ============================== A7.2 Members ==============================

type MKey = "name" | "email" | "projects" | "last" | "status";
const MGET: Record<MKey, (p: AdminPerson) => string | number> = {
  name: (p) => p.name.toLowerCase(), email: (p) => p.email, projects: (p) => p.projects.length,
  last: (p) => p.last_active ?? "", status: (p) => p.status,
};
const MCOLS: [MKey, string, string?][] = [["name", "Name"], ["email", "Email"], ["projects", "Projects", "num"], ["last", "Last active"], ["status", "Status", "center"]];

function MembersTab({ route }: { route: AdminRoute }) {
  const { data, error, reload } = useRpc("admin_members");
  const [q, setQ] = useState("");
  const [code, setCode] = useState(route.code ?? "");
  const [sort, setSort] = useState<SortState<MKey>>({ key: null, dir: "asc" });
  const [sel, setSel] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [hi, setHi] = useState<number | null>(null);
  useEffect(() => { if (data && code && !data.projects.some((p) => p.code === code)) setCode(""); }, [data, code]);
  const rows = useMemo(() => {
    const list = (data?.people ?? []).filter((p) => memberMatches(p, q, code, ""));
    return sort.key ? sortBy(list, MGET[sort.key], sort.dir) : list;
  }, [data, q, code, sort]);
  useEffect(() => setHi(null), [q, code, sort]);
  const pick = (id: string | null) => requestLeave(() => setSel(id));      // a panel with edits asks first (v0.4.3)
  useListKeys({ count: rows.length, hi, setHi, onEnter: (i) => pick(rows[i].id), active: !adding });
  useEsc("popup", () => { if (!sel) return false; pick(null); return true; }, !!sel && !adding);
  const person = data?.people.find((p) => p.id === sel);
  const count = `${rows.length} ${rows.length === 1 ? "person" : "people"}`;
  return (
    <>
      <AdminHeader tab="members" actions={<button className="btn primary" onClick={() => setAdding(true)}><Icon name="add" /> Add member</button>} />
      {error && <ErrorBox message={error} />}
      {!data ? (!error && <Loading />) : <>
        <div className="row between filters-row">
          <div className="row gap8">
            <SearchBox placeholder="Search name or email" value={q} onChange={setQ} width={240} />
            <FilterDropdown label="Project" allLabel="All projects" value={code} onChange={setCode}
              options={data.projects.map((p) => ({ value: p.code, label: p.code }))} />
          </div>
          <span className="muted small" data-testid="people-count">{count}</span>
        </div>
        <div className={person ? "admin-grid" : ""}>
          <div className="card list-card">
            <table className="fixed-cols members-table" data-testid="members-table">
              <colgroup><col style={{ width: 160 }} /><col /><col style={{ width: 82 }} /><col style={{ width: 104 }} /><col style={{ width: 108 }} /><col style={{ width: 28 }} /></colgroup>
              <thead><tr>
                {MCOLS.map(([k, label, cls]) => <SortTh key={k} label={label} className={cls} arrow={sortArrow(sort, k)} onClick={() => setSort(nextSort(sort, k))} />)}
                <th className="tip-th"><InfoIcon tip={["↕ = sortable column. Click a header: ↑ first, ↓ second, third click back to A–Z by name.", "↑ / ↓ move through the list; Enter opens the person.", "Projects = how many projects the person is on."]} /></th>
              </tr></thead>
              <tbody>{rows.map((p, k) => (
                <tr key={p.id} data-row={k} className={`clickable${p.id === sel ? " active" : ""}${k === hi ? " kbd" : ""}${p.active ? "" : " dim"}`} onClick={() => pick(p.id)}>
                  <td className="ellipsis">{p.name}</td><td className="muted ellipsis">{p.email}</td>
                  <td className="num">{p.projects.length}</td><td className="nowrap">{p.last_active ? formatDate(p.last_active) : "–"}</td>
                  <td className="center"><span className={`pill ${STATUS_PILL[p.status]}`}>{p.status}</span></td><td />
                </tr>))}
              </tbody>
            </table>
            {rows.length === 0 && <p className="muted pad">No one matches</p>}
          </div>
          {person && <MemberPanel key={person.id} person={person} data={data} onCancel={() => pick(null)} onSaved={() => reload()} onRemoved={() => { pick(null); reload(); }} />}
        </div>
      </>}
      {adding && <AddMemberModal prefill={code || undefined} onClose={() => setAdding(false)} onDone={(id) => { setAdding(false); reload(); setSel(id); }} />}
    </>
  );
}

/** v0.9.1 (round 32 #5, Kyle 03/Nov/26) A7.2.5 Remove member — always asks.
 *  Never signed in → deleted completely. Has used BuildNexx → their login is deleted, their work stays as "Removed member";
 *  typing their first name confirms (it cannot be undone). Rules in SQL (0023 admin_member_remove). */
export const confirmWord = (name: string) => (name.trim().split(/\s+/)[0] ?? "").toUpperCase();
function RemoveMemberModal({ person, onClose, onDone }: { person: AdminPerson; onClose: () => void; onDone: () => void }) {
  const never = !person.last_active;
  const first = person.name.trim().split(/\s+/)[0] ?? person.name;
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const ok = never || typed.trim().toUpperCase() === confirmWord(person.name);
  const go = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await auth.removeMember(person.id);
      toast(r.warning ?? (r.mode === "deleted" ? `${person.name} deleted` : `${person.name} removed — their work now shows "Removed member"`), r.warning ? 8000 : undefined);
      onDone();
    } catch (e) { setErr(errText(e)); setBusy(false); }
  };
  return (
    <Modal title={`Remove ${first}?`} onClose={onClose} className="small-wide remove-member">
      <div data-testid={`remove-${never ? "invited" : "active"}`}>
        {never ? <>
          <p><b>{person.name}</b> ({person.email}) never signed in.</p>
          <p className="muted">The invitation stops working and the person is deleted completely. You can add the address again later.</p>
        </> : <>
          <p><b>{person.name}</b> ({person.email}) has used BuildNexx (last active {formatDate(person.last_active!)}).</p>
          <ul className="muted remove-list">
            <li>Their login is deleted and they are taken off every project.</li>
            <li>Their issues, log entries and Activity stay, shown as <b className="ink">Removed member</b>.</li>
            <li>This cannot be undone. Someone who may come back: turn <b className="ink">Can sign in</b> off instead.</li>
          </ul>
          <label className="field"><span>Type <b>{confirmWord(person.name)}</b> to confirm</span>
            <input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={confirmWord(person.name)} aria-label="Type the name to confirm" autoFocus /></label>
        </>}
      </div>
      {err && <ErrorBox message={err} />}
      <div className="modal-foot"><button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn primary danger-fill" disabled={!ok || busy} onClick={go}>{never ? "Remove" : "Remove member"}</button></div>
    </Modal>
  );
}

/** A7.2 Edit member — identity header, Profile / Access / Sign-in sections, one button row (round 16b). */
function MemberPanel({ person, data, onCancel, onSaved, onRemoved }: {
  person: AdminPerson; data: AdminMembers; onCancel: () => void; onSaved: () => void; onRemoved: () => void;
}) {
  const { me } = useSession();
  const [name, setName] = useState(person.name);
  const [email, setEmail] = useState(person.email);
  const [active, setActive] = useState(person.active);
  const [company, setCompany] = useState(person.company_role ?? "");
  const [askRole, setAskRole] = useState<string | null>(null);
  const [managing, setManaging] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const invited = person.status === "Invited";
  const roleLabel = companyLabel(person.company_role, person.is_super_admin);
  const dirty = name !== person.name || email !== person.email || active !== person.active || company !== (person.company_role ?? "");
  const save = async (): Promise<boolean> => {
    setBusy(true); setErr(null);
    let ok = false;
    try {
      const args: Record<string, unknown> = { id: person.id };
      if (!rootLocked) Object.assign(args, { full_name: name, email, active });
      if (!person.is_super_admin && company !== (person.company_role ?? "")) args.company_role = company;
      await rpc("admin_member_update", args);
      toast("Saved"); onSaved(); ok = true;
    } catch (e) { setErr(errText(e)); }
    setBusy(false);
    return ok;
  };
  useLeaveGuard(dirty, save);                                    // v0.4.3: leaving with edits asks first
  const mail = async () => {
    setErr(null);
    try {
      if (invited) { const r = await auth.resendInvite(person.id); toast(inviteToast(r, true)); onSaved(); }
      else {
        const r = await rpc("admin_member_reset_password", { id: person.id });     // permission check, then the email
        await auth.sendPasswordReset(r.email); toast(`Password reset link sent to ${r.email}`);
      }
    } catch (e) { setErr(errText(e)); }
  };
  // Round 17 #6: every company-role change asks first (also Project head → Project member with no projects).
  const pickCompany = (v: string) => { if (v !== company) setAskRole(v); };
  const showProjects = !person.is_super_admin && !EVERY_PROJECT(person.company_role);
  // Administrator: only the super admin appoints; an Administrator never removes their own rights (0010).
  const roleOptions = COMPANY_ROLES.filter((c) => c.value !== "admin" || me.is_super_admin || person.company_role === "admin");
  const roleLocked = person.is_super_admin || (person.company_role === "admin" && person.id === me.id);
  const rootLocked = person.is_super_admin && !me.is_super_admin;           // nobody but the super admin changes the super admin
  return (
    <aside className="card admin-panel member-panel" aria-label="Edit member">
      <div className="mp-head">
        <span className="avatar big">{person.name[0]}</span>
        <div className="grow"><b>{person.name}</b><small>{roleLabel}</small></div>
        <span className={`pill ${STATUS_PILL[person.status]}`}>{person.status}</span>
      </div>
      <div className="panel-body mp-body">
        <section><p className="mp-cap">Profile</p>
          <label className="field"><span>Full name</span><input value={name} disabled={rootLocked} onChange={(e) => setName(e.target.value)} aria-label="Full name" /></label>
          <label className="field"><span>Email</span><input value={email} disabled={rootLocked} onChange={(e) => setEmail(e.target.value)} aria-label="Email" /></label>
        </section>
        <section><p className="mp-cap">Access</p>
          <label className="field"><span className="field-label">Company role <InfoIcon tip={TIPS.company} /></span>
            {roleLocked
              ? <input value={roleLabel} disabled aria-label="Company role" />
              : <select value={company} onChange={(e) => pickCompany(e.target.value)} aria-label="Company role">
                  {roleOptions.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>}
          </label>
          {/* round 28 #2 (Kyle 19/Oct/26): no project list here — Manage opens A7.2.2, which lists them */}
          {showProjects
            ? <div className="mp-row" data-testid="member-projects"><span>Projects ({person.projects.length})</span>
                <button type="button" className="btn link accent" onClick={() => setManaging(true)}>Manage</button></div>
            : <div className="mp-row"><span>Projects</span><span className="muted" data-testid="every-project">Every project ({data.projects.length})</span></div>}
        </section>
        <section><p className="mp-cap">Sign-in</p>
          <div className="mp-row"><span className="field-label">Can sign in <InfoIcon tip={TIPS.signIn} /></span>
            <Switch on={active} label="Can sign in" disabled={person.id === me.id || person.is_super_admin} onChange={setActive} /></div>
          <div className="mp-row"><span>{invited ? "Last invitation sent" : "Last active"}</span>
            <b className="date">{invited ? (person.invited_on ? formatDate(person.invited_on) : "–") : person.last_active ? formatDate(person.last_active) : "–"}</b></div>
          <div className="mp-row"><span className="field-label">{invited ? "Invitation" : "Password"} <InfoIcon tip={TIPS.mail} /></span>
            <button type="button" className="btn link accent" onClick={mail}>{invited ? "Resend invite" : "Reset password"}</button></div>
        </section>
        {err && <ErrorBox message={err} />}
      </div>
      <div className="panel-foot">
        {/* v0.9.1 (round 32 #5): Remove — never the super admin or yourself; an Administrator only by the super admin */}
        {!person.is_super_admin && person.id !== me.id && (person.company_role !== "admin" || me.is_super_admin) &&
          <span className="row gap6 mp-remove"><button type="button" className="btn link danger" data-testid="remove-member" onClick={() => setRemoving(true)}>Remove</button><InfoIcon tip={TIPS.remove} /></span>}
        <button className="btn" onClick={onCancel}>Cancel</button>
        <button className="btn primary" disabled={busy} onClick={save}>Save changes</button>
      </div>
      {removing && <RemoveMemberModal person={person} onClose={() => setRemoving(false)} onDone={() => { setRemoving(false); onRemoved(); }} />}
      {askRole !== null && (
        <Modal title="Change company role?" onClose={() => setAskRole(null)} className="small role-confirm">
          <p className="role-change" data-testid="role-change"><b>{person.name}</b><span className="pill p-tag">{companyLabel(company)}</span><span className="muted">→</span>
            <span className="pill p-crit-soft">{companyLabel(askRole)}</span></p>
          <p className="muted">{roleChangeText(company || null, askRole, person.projects.some((p) => p.role_id))}</p>
          <div className="modal-foot"><button className="btn" onClick={() => setAskRole(null)}>Keep</button>
            <button className="btn primary" onClick={() => { setCompany(askRole); setAskRole(null); }}>Change</button></div>
        </Modal>
      )}
      {managing && <ManageProjectsModal person={person} data={data} onClose={() => setManaging(false)} onSaved={() => { setManaging(false); onSaved(); }} />}
    </aside>
  );
}

/** A7.2.3 Add member — company role first (super admin / Administrators), then projects (optional, round 17 #5).
 *  People with the Project setup switch (from Manage members) still need one of their projects. Used from Members and
 *  Manage members. v0.8.0: "Add & send invite" — a new person gets the invite email (auth.invite). */
export function AddMemberModal({ prefill, onClose, onDone }: { prefill?: string; onClose: () => void; onDone: (id: string) => void }) {
  const { me } = useSession();
  const { data, error } = useRpc("admin_members");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [askAdmin, setAskAdmin] = useState(false);
  // v0.9.2 (round 33 #1, board A7.2.3): projects are picked like Manage projects — Assigned (the project it was opened
  // from) on top, every other project below with a tick and a role
  const [rows, setRows] = useState<PickRow[]>([]);
  const [q, setQ] = useState("");
  const [openTop, setOpenTop] = useState(true);
  const [openOthers, setOpenOthers] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const limited = !me.is_admin;
  const head = company === "project_head", every = EVERY_PROJECT(company);
  useEffect(() => {
    if (!data || rows.length) return;
    const only = limited && !prefill && data.projects.length === 1 ? data.projects[0].code : null;
    setRows(data.projects.map((pr) => { const on = pr.code === prefill || pr.code === only; return { key: pr.code, on, role: "", was: { on: pr.code === prefill, role: "" } }; }));
  }, [data]);   // eslint-disable-line react-hooks/exhaustive-deps
  const setRow = (key: string, patch: Partial<PickRow>) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const pname = (c: string) => data?.projects.find((p) => p.code === c)?.name ?? c;
  const match = (c: string) => `${c} ${pname(c)}`.toLowerCase().includes(q.trim().toLowerCase());
  const plabel = (c: string) => <><b className="pick-code">{c}</b><span className="ellipsis">{pname(c)}</span></>;
  const top = rows.filter((r) => r.was.on && match(r.key)), others = rows.filter((r) => !r.was.on && match(r.key));
  const ticked = rows.filter((r) => r.on).length;
  const pickEmail = (v: string) => {
    setEmail(v);
    const hit = data?.people.find((p) => p.email.toLowerCase() === v.trim().toLowerCase());
    if (hit && !name.trim()) setName(hit.name);
  };
  const pickCompany = (v: string) => (v === "admin" ? setAskAdmin(true) : setCompany(v));   // appointing an Administrator asks first
  const typed = !!(name.trim() || email.trim() || company || rows.some((r) => r.on !== r.was.on || r.role));
  const { close, confirm } = useAskClose(typed, onClose);
  const submit = async () => {
    const list: Assignment[] = every ? [] : rows.filter((r) => r.on).map((r) => ({ code: r.key, role_id: head ? "project_head" : r.role }));
    const problems = addMemberProblems(name, email, list, limited);
    if (problems.length) return setErr(problems.join(" · "));
    setBusy(true); setErr(null);
    try {
      const args: Record<string, unknown> = { full_name: name.trim(), email: email.trim(), assignments: list };
      if (me.is_admin) args.company_role = company;
      const r = await auth.invite(args);                  // v0.8.0: Add & send invite (a new person gets the email)
      toast(inviteToast(r)); onDone(r.id);
    }
    catch (e) { setErr(errText(e)); setBusy(false); }
  };
  const options = COMPANY_ROLES.filter((c) => c.value !== "admin" || me.is_super_admin);
  const roles = projectRoles(data?.roles ?? []);
  return (
    <Modal title="Add member" info={TIPS.addMember} onClose={close} className="pick-modal add-member">
      {error && <ErrorBox message={error} />}
      <div className={me.is_admin ? "grid3" : "grid2"}>
        <label className="field"><span>Full name</span><input autoFocus value={name} onChange={(e) => setName(e.target.value)} aria-label="Full name" /></label>
        <label className="field"><span>Email</span><input type="email" value={email} onChange={(e) => pickEmail(e.target.value)} aria-label="Email" list="buildnexx-people-emails" /></label>
        {me.is_admin && <label className="field"><span>Company role</span>
          <select value={company} onChange={(e) => pickCompany(e.target.value)} aria-label="Company role">
            {options.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select></label>}
      </div>
      <datalist id="buildnexx-people-emails">{data?.people.map((p) => <option key={p.id} value={p.email}>{p.name}</option>)}</datalist>
      {every ? <p className="muted small" data-testid="every-project">Every project — GM and Administrators are on all of them</p> : <>
        <div className="row between gap12"><span className="small muted nowrap">{head ? "Projects" : "Projects & role"}{!limited && " (optional)"}</span>
          <SearchBox placeholder="Search code or project" value={q} onChange={setQ} width={360} /></div>
        <Section title={`Assigned (${top.length})`} open={openTop} onToggle={() => setOpenTop(!openTop)}>
          {top.map((r) => <PickLine key={r.key} name={r.key} tick="space" label={plabel(r.key)} role={r.role} roles={roles} roleFixed={head ? "Project head" : undefined}
            onRole={(v) => setRow(r.key, { role: v })} />)}
          {top.length === 0 && <p className="muted small pad">None yet — tick projects below and pick a role</p>}
        </Section>
        <Section title={`Other projects (${others.length})`} open={openOthers} onToggle={() => setOpenOthers(!openOthers)} scroll>
          {others.map((r) => <PickLine key={r.key} name={r.key} tick={r.on} label={plabel(r.key)} role={r.role} roles={roles} roleFixed={head ? "Project head" : undefined}
            onTick={(v) => setRow(r.key, { on: v })} onRole={(v) => setRow(r.key, { role: v, on: true })} />)}
        </Section>
      </>}
      {err && <ErrorBox message={err} />}
      <div className="modal-foot"><span className="muted small grow" data-testid="ticked-count">{every ? "" : `${ticked} project${ticked === 1 ? "" : "s"} ticked`}</span><button className="btn" onClick={close}>Cancel</button><button className="btn primary" disabled={busy || !data} onClick={submit}>{busy ? "Sending…" : "Add & send invite"}</button></div>
      {confirm}
      {askAdmin && (
        <Modal title="Change company role?" onClose={() => setAskAdmin(false)} className="small role-confirm">
          <p className="role-change"><b>{name.trim() || "New member"}</b><span className="pill p-tag">{companyLabel(company)}</span><span className="muted">→</span>
            <span className="pill p-crit-soft">Administrator</span></p>
          <p className="muted">{roleChangeText(company || null, "admin", false)}</p>
          <div className="modal-foot"><button className="btn" onClick={() => setAskAdmin(false)}>Keep</button>
            <button className="btn primary" onClick={() => { setCompany("admin"); setAskAdmin(false); }}>Change</button></div>
        </Modal>
      )}
    </Modal>
  );
}

// ============================== A7.3 Roles & access (+ A7.3.1 Add role) ==============================

const ACC_LABEL: Record<Access, string> = { edit: "Edit", view: "View", none: "No access" };
const UA_LABEL: Record<Unassigned, string> = { edit: "Edit", view: "View", hidden: "Hidden" };
const UA_CLASS: Record<Unassigned, string> = { edit: "acc-edit", view: "acc-view", hidden: "acc-none" };

function RolesTab() {
  const { data, error, reload } = useRpc("admin_roles");
  const [edits, setEdits] = useState<Record<string, Partial<AdminRole>>>({});
  const [err, setErr] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState<AdminRole | null>(null);
  const roles = useMemo(() => (data?.roles ?? []).map((r) => ({ ...r, ...edits[r.id], access: { ...r.access, ...edits[r.id]?.access } })), [data, edits]);
  const changes = data ? changedRoles(data.roles, roles) : [];
  const edit = (r: AdminRole, patch: Partial<AdminRole>) => setEdits({ ...edits, [r.id]: { ...edits[r.id], ...patch, access: { ...edits[r.id]?.access, ...patch.access } as Record<string, Access> } });
  const save = async (): Promise<boolean> => {
    setErr(null);
    try { await rpc("admin_roles_save", { roles: changes }); setEdits({}); reload(); toast("Saved"); return true; }
    catch (e) { setErr(errText(e)); return false; }
  };
  useLeaveGuard(changes.length > 0, save);                       // v0.4.3: leaving with edits asks first
  return (
    <>
      <AdminHeader tab="roles" actions={<>
        <button className="btn" onClick={() => setAdding(true)}><Icon name="add" /> Add role</button>
        <button className="btn primary" disabled={changes.length === 0} onClick={save}>Save changes</button>
      </>} />
      {(error || err) && <ErrorBox message={(error ?? err)!} />}
      {!data ? (!error && <Loading />) : (
        <div className="card list-card">
          <table className="roles-table" data-testid="roles-table">
            <thead><tr>
              <th>Role</th>
              {data.modules.map((m, i) => <th key={m.id} className="num">{m.name}{!built(m.id) && <span className="soon">soon</span>}{i === 0 && <> <InfoIcon tip={TIPS.access} /></>}</th>)}
              <th className="num nowrap">Unassigned projects <InfoIcon tip={TIPS.unassigned} /></th>
              <th className="num">Sign-off <InfoIcon tip={TIPS.signoff} /></th>
              <th className="num">Project setup <InfoIcon tip={TIPS.setup} /></th>
              <th className="num">Members</th><th style={{ width: 40 }} />
            </tr></thead>
            <tbody>
              <tr data-role="super_admin">
                <td>Super admin <Icon name="lock" /></td>
                {data.modules.map((m) => <td key={m.id} className="num"><span className="acc acc-edit locked">Edit</span></td>)}
                <td className="num"><span className="acc acc-edit locked">Edit</span></td>
                <td className="num"><Switch on label="Super admin sign-off" disabled /></td>
                <td className="num"><Switch on label="Super admin project setup" disabled /></td>
                <td className="num">{data.super_admins}</td><td />
              </tr>
              {roles.map((r) => r.id === "admin" ? (
                <tr key={r.id} data-role={r.id}>
                  <td>{r.name} <Icon name="lock" /><small className="role-kind">company role</small></td>
                  {data.modules.map((m) => <td key={m.id} className="num"><span className="acc acc-edit locked">Edit</span></td>)}
                  <td className="num"><span className="acc acc-edit locked">Edit</span></td>
                  <td className="num"><Switch on label="Administrator sign-off" disabled /></td>
                  <td className="num"><Switch on label="Administrator project setup" disabled /></td>
                  <td className="num">{r.members}</td><td />
                </tr>
              ) : (
                <tr key={r.id} data-role={r.id}>
                  <td>{r.name}<small className="role-kind">{r.kind === "company" ? "company role" : "project role"}</small></td>
                  {data.modules.map((m) => (
                    <td key={m.id} className="num">
                      <select className={`acc acc-${r.access[m.id] ?? "none"}`} aria-label={`${r.name} · ${m.name}`} value={r.access[m.id] ?? "none"}
                        onChange={(e) => edit(r, { access: { [m.id]: e.target.value as Access } })}>
                        {(["edit", "view", "none"] as Access[]).map((a) => <option key={a} value={a}>{ACC_LABEL[a]}</option>)}
                      </select>
                    </td>))}
                  <td className="num">
                    <select className={`acc ${UA_CLASS[r.unassigned_access]}`} aria-label={`${r.name} · Unassigned projects`} value={r.unassigned_access}
                      onChange={(e) => edit(r, { unassigned_access: e.target.value as Unassigned })}>
                      {(["edit", "view", "hidden"] as Unassigned[]).map((a) => <option key={a} value={a}>{UA_LABEL[a]}</option>)}
                    </select>
                  </td>
                  <td className="num"><Switch on={r.can_signoff} label={`${r.name} sign-off`} onChange={(v) => edit(r, { can_signoff: v })} /></td>
                  <td className="num"><Switch on={r.can_setup} label={`${r.name} project setup`} onChange={(v) => edit(r, { can_setup: v })} /></td>
                  <td className="num">{r.members}</td>
                  <td>{r.is_custom && r.members === 0 && <button className="btn icon mini" aria-label={`Delete role ${r.name}`} onClick={() => setDeleting(r)}><Icon name="close" /></button>}</td>
                </tr>))}
            </tbody>
          </table>
        </div>
      )}
      {adding && data && <AddRoleModal roles={roles} modules={data.modules} onClose={() => setAdding(false)} onDone={() => { setAdding(false); reload(); }} />}
      {deleting && <DeleteRoleModal role={deleting} onClose={() => setDeleting(null)} onDone={() => { setDeleting(null); reload(); }} />}
    </>
  );
}

function AddRoleModal({ roles, modules, onClose, onDone }: { roles: AdminRole[]; modules: { id: string; name: string }[]; onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState("");
  const [from, setFrom] = useState(roles.find((r) => r.id === "bim_coordinator")?.id ?? roles[0]?.id ?? "");
  const [err, setErr] = useState<string | null>(null);
  const { close, confirm } = useAskClose(!!name.trim(), onClose, () => submit());
  const submit = async () => {
    if (!name.trim()) return setErr("Type the role name");
    try { await rpc("admin_role_add", { name: name.trim(), copy_from: from }); onDone(); } catch (e) { setErr(errText(e)); }
  };
  return (
    <Modal title="Add role" info={TIPS.addRole} onClose={close} className="small-wide">
      <label className="field"><span>Role name</span><input autoFocus value={name} onChange={(e) => setName(e.target.value)} aria-label="Role name"
        onKeyDown={(e) => { if (e.key === "Enter") submit(); }} /></label>
      <label className="field"><span>Start from</span>
        <select value={from} onChange={(e) => setFrom(e.target.value)} aria-label="Start from">{roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select>
        <small className="muted tiny" data-testid="copy-summary">{copySummary(roles.find((r) => r.id === from), modules, built)}</small>
      </label>
      {err && <ErrorBox message={err} />}
      <div className="modal-foot"><button className="btn" onClick={close}>Cancel</button><button className="btn primary" onClick={submit}>Add role</button></div>
      {confirm}
    </Modal>
  );
}

function DeleteRoleModal({ role, onClose, onDone }: { role: AdminRole; onClose: () => void; onDone: () => void }) {
  const [err, setErr] = useState<string | null>(null);
  const go = async () => { try { await rpc("admin_role_delete", { id: role.id }); onDone(); } catch (e) { setErr(errText(e)); } };
  return (
    <Modal title={`Delete role ${role.name}?`} onClose={onClose} className="small">
      <p className="muted">Nobody holds this role.</p>
      {err && <ErrorBox message={err} />}
      <div className="modal-foot"><button className="btn" onClick={onClose}>Keep</button><button className="btn danger" onClick={go}>Delete role</button></div>
    </Modal>
  );
}

/** Used by A6 Project setup (Part 4): "👥 Manage members" opens ManageMembersModal; this link stays for direct use. */
